import type { AnyAction } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';

import type { BaseReport, Col, Item, Report, Row } from '@webapp/account/src/resources/reports/reports';
import { initialBaseReport } from '@webapp/account/src/resources/reports/reports';

import { mapNpsLabel } from '@webapp/common/lib/ui';
import { updateItemInArray } from '@webapp/common/lib/utils';
import type { Question } from '@webapp/common/resources/entities/question';
import { QuestionType } from '@webapp/common/resources/survey';

export enum FilterType {
    EXCLUDE = 'EXCLUDE',
    INCLUDE = 'INCLUDE',
}

export enum FilterFieldType {
    COL = 'COL',
    ROW = 'ROW',
    CELL = 'CELL',
    // ITEM = 'ITEM',
    COMMENT = 'COMMENT',
    WORD_CLOUD = 'WORD_CLOUD',
    COMMENT_CLOUD = 'COMMENT_CLOUD',
    SELF = 'SELF',
    CATEGORY = 'CATEGORY',
}

export interface FilterSet {
    id: number | string;
    name: string;
    type: FilterType;
    answerGroupId: number;
    filterFieldType: FilterFieldType;
    itemId: number | string;
    value: any;
    direction: FilterFieldType;
    spoiled: boolean;
}

export const initialSet: Readonly<FilterSet> = {
    id: 0,
    name: '',
    type: null,
    answerGroupId: 0,
    filterFieldType: null,
    itemId: 0,
    value: null,
    direction: null,
    spoiled: false,
};

interface FilterRequest {
    questionId: number;
    direction?: FilterFieldType;
    answerId?: number;
    answerInt?: number; // stars
    questionType?: QuestionType;
    itemId?: number; // response id, self/comment ?? rename
    type?: FilterType;
    value?: any; // TODO purge
    answerGroupId?: number;
    filterFieldType?: FilterFieldType;
    responseId?: number;
    answerStr?: string;
    routeHash?: string;
}

export const getRequestFiltersByType = (
    filters: Array<FilterRequest>, // TODO <FilterSet>
    filterType: FilterType,
): Array<FilterRequest> =>
    filters
        .filter(({ type }) => type === filterType)
        .map((filter) => {
            const { answerGroupId, answerId, direction, filterFieldType, itemId, questionId, questionType, value } =
                filter;

            if (filterFieldType === FilterFieldType.COMMENT) {
                return {
                    questionId,
                    responseId: itemId,
                };
            }

            switch (questionType) {
                case QuestionType.NUMBER:
                    return {
                        questionId,
                        answerInt: value,
                    };
                case QuestionType.MATRIX_TEXT_ANSWER:
                    return { questionId, responseId: itemId };
                case QuestionType.MATRIX_SINGLE_ANSWER:
                case QuestionType.MATRIX_FEW_ANSWERS:
                    if (filterFieldType === FilterFieldType.SELF) {
                        return {
                            questionId,
                            responseId: answerId,
                        };
                    }
                    switch (direction) {
                        case FilterFieldType.ROW:
                            return { questionId, answerId };
                        case FilterFieldType.COL:
                            return { questionId, answerGroupId };
                        default:
                            // cell
                            return { questionId, answerId: itemId, answerGroupId };
                    }
                case QuestionType.SCALE:
                    return { questionId, answerGroupId: answerId };
                case QuestionType.SMILE:
                    return {
                        questionId,
                        answerId,
                        responseId: !!filterFieldType ? itemId : undefined,
                    };
                case QuestionType.STAR:
                case QuestionType.NPS:
                    return { questionId, answerInt: answerId };
                case QuestionType.RANK:
                case QuestionType.TEST_RANK:
                    switch (direction) {
                        case FilterFieldType.ROW:
                            return { questionId, answerId };
                        case FilterFieldType.COL:
                            return { questionId, answerInt: answerGroupId };
                        default:
                            // cell
                            return { questionId, answerId: itemId, answerInt: answerGroupId };
                    }
                case QuestionType.MATRIX_RATING:
                    switch (direction) {
                        case FilterFieldType.ROW:
                            return { questionId, answerId };
                        case FilterFieldType.COL:
                            return { questionId, answerInt: answerGroupId };
                        default:
                            // cell
                            return { questionId, answerId: itemId, answerInt: answerGroupId };
                    }
                case QuestionType.MATRIX_DROPDOWN_LIST:
                    return { questionId, responseId: itemId };
                case QuestionType.NAME:
                case QuestionType.EMAIL:
                case QuestionType.DATETIME:
                case QuestionType.PHONE:
                    return { questionId, responseId: itemId };
                case QuestionType.GROUP_FREE_ANSWERS:
                    return { questionId, answerId, responseId: itemId };
                case QuestionType.ONE_OF_LIST:
                    return {
                        questionId,
                        answerId,
                        by: filterFieldType || undefined,
                        responseId: !!filterFieldType ? answerId : undefined,
                    };
                case QuestionType.RESIDENCE:
                case QuestionType.NESTED_LIST:
                    return {
                        questionId,
                        answerId,
                        answerStr: String(answerGroupId),
                    };
                case QuestionType.AGREEMENT:
                    return {
                        questionId,
                        answerId,
                    };
                case QuestionType.SEM_DIFF:
                    switch (direction) {
                        case FilterFieldType.ROW:
                            return {
                                questionId,
                                answerId,
                                answerGroupId,
                                responseId: !!filterFieldType ? itemId : undefined,
                            };
                        case FilterFieldType.COL:
                            return { questionId, answerGroupId };
                        default:
                            // cell
                            return { questionId, answerGroupId, answerId: itemId };
                    }
                case QuestionType.SHORT_TEXT:
                case QuestionType.LONG_TEXT:
                case QuestionType.TEST_TEXT:
                    return {
                        questionId,
                        responseId: answerId,
                    };
                case QuestionType.SELECT_FEW_IMAGE:
                case QuestionType.SELECT_ONE_IMAGE:
                    return {
                        questionId,
                        answerId,
                        responseId: !!filterFieldType ? answerId : undefined,
                    };
                case QuestionType.TEXT_PERCEPTION:
                    return { questionId, answerJson: value };
                case QuestionType.PAIR_COMPARISON:
                    return { questionId, answerId: itemId, answerInt: answerGroupId, answerBool: 1 };
                case QuestionType.FIRST_CLICK:
                    return { questionId, answerId };
                case QuestionType.FIGMA_TEST:
                    if (typeof answerId === 'string') {
                        return { questionId, routeHash: answerId };
                    }
                    return { questionId, answerBool: value !== null && value !== undefined ? Number(value) : value };
                case QuestionType.CARD_SORTING: {
                    switch (direction) {
                        case FilterFieldType.ROW:
                            return { questionId, answerId };
                        case FilterFieldType.COL:
                            return { questionId, categoryId: answerGroupId };
                        default:
                            // cell
                            return { questionId, categoryId: answerGroupId, answerId: itemId };
                    }
                }
                case QuestionType.KANO:
                    if (filterFieldType === FilterFieldType.CATEGORY) {
                        return {
                            questionId,
                            answerId: itemId,
                            answerCategory: value?.category,
                        };
                    }
                    // Default Kano filtering by row
                    return { questionId, answerId };
                case QuestionType.CONJOINT:
                    return { questionId, answerId, answerBool: 1 };
                default:
                    return {
                        questionId,
                        answerId,
                        answerGroupId,
                        responseId: !!filterFieldType ? itemId : undefined,
                    };
            }
        });

export const calcFilters = (filters: Array<BaseReport>, reports: Array<Report | Question>): Array<any> =>
    filters.flatMap(({ id: questionId, sets }) => {
        // reports может не прийти от вызывающего (FRONT-2A9C и родственные:
        // getWordStat → getFilterParams без questions ронял всю страницу).
        const curQuestion = reports?.find(({ id }) => id === questionId); // TODO wtf
        return sets.map(({ answerGroupId, direction, filterFieldType, id: answerId, itemId, type, value }) => ({
            answerId,
            direction,
            questionId,
            type,
            filterFieldType,
            itemId,
            questionType: curQuestion?.type,
            answerGroupId,
            value,
        }));
    });

const calcFilterType = (dir: any, filterType: FilterType): FilterType => {
    let newFilterType;

    if (!dir.filterType) {
        newFilterType = filterType;
    } else {
        if (dir.filterType === filterType) {
            newFilterType = null;
        } else {
            newFilterType = dir.filterType === FilterType.EXCLUDE ? FilterType.INCLUDE : FilterType.EXCLUDE;
        }
    }

    return newFilterType;
};

export const toggleFilterSet = (
    filters: Array<BaseReport>,
    action: AnyAction,
    report: Report,
    filterType: FilterType,
): Array<BaseReport> => {
    const { col, filterFieldType, item, itemId, row } = action;
    const byCell = Boolean(row && col);
    const filter = filters.find(({ id }) => id === report.id);
    const residence = report.type === QuestionType.RESIDENCE;
    const nested = report.type === QuestionType.NESTED_LIST;
    const withItems = residence || nested;
    const number = report.type === QuestionType.NUMBER;
    const semDiff = report.type === QuestionType.SEM_DIFF;
    let existingSetIdx;

    const newSet: FilterSet = {
        ...cloneDeep(initialSet),
        itemId: item ? item.id : itemId,
    };

    switch (filterFieldType) {
        case FilterFieldType.WORD_CLOUD:
            existingSetIdx =
                filter &&
                filter.sets.findIndex(
                    ({ filterFieldType, id }) => id === itemId && filterFieldType === FilterFieldType.WORD_CLOUD,
                );
            newSet.id = itemId;
            newSet.filterFieldType = filterFieldType;
            newSet.name = itemId;
            newSet.type = filterType;
            break;
        case FilterFieldType.COMMENT:
            const comment = report.comments.find(({ id }) => id === itemId);
            existingSetIdx =
                filter &&
                filter.sets.findIndex(
                    ({ filterFieldType, id }) => id === comment.id && filterFieldType === FilterFieldType.COMMENT,
                );
            newSet.id = itemId;
            newSet.itemId = itemId;
            newSet.filterFieldType = filterFieldType;
            newSet.name = comment.comment;
            newSet.type = calcFilterType(comment, filterType);
            break;
        case FilterFieldType.SELF:
            const self = item.self.find(({ id }: { id: number }) => id === itemId);
            existingSetIdx =
                filter &&
                filter.sets.findIndex(
                    ({ filterFieldType, id }) => id === itemId && filterFieldType === FilterFieldType.SELF,
                );
            newSet.id = itemId;
            newSet.itemId = item.id;
            newSet.value = self.singleValue; // number
            newSet.filterFieldType = filterFieldType;
            newSet.type = calcFilterType(self, filterType);
            // TODO better name mapping
            newSet.name =
                Array.isArray(self.cells) && self.cells.length > 0
                    ? [self.cells[0].value1, self.cells[0].value2, self.cells[0].singleValue].join(' - ')
                    : self.values && self.values.length > 0
                    ? self.values.map(({ value1 }: { value1: string }) => value1).join(' - ')
                    : self.singleValue || self.value1;
            break;
        case FilterFieldType.CATEGORY:
            // Kano category filtering
            const categoryRow = item.rows.find(({ id }: { id: number }) => id === row.id);
            const categoryValue = action.value?.category;
            existingSetIdx =
                filter &&
                filter.sets.findIndex(
                    ({ filterFieldType, itemId, value }) =>
                        itemId === row.id &&
                        filterFieldType === FilterFieldType.CATEGORY &&
                        value?.category === categoryValue,
                );
            // Create unique id for each category in a row (rowId_categoryCode)
            newSet.id = `${row.id}_${categoryValue}`;
            newSet.itemId = row.id;
            newSet.filterFieldType = filterFieldType;
            newSet.direction = FilterFieldType.CELL;
            newSet.type = calcFilterType(
                categoryRow?.cells?.find((c) => c.value?.category === categoryValue) || {},
                filterType,
            );
            newSet.name = categoryRow.value1;
            newSet.value = { category: categoryValue };
            newSet.answerGroupId = undefined;
            break;
        default:
            if (byCell) {
                existingSetIdx =
                    filter &&
                    filter.sets.findIndex(
                        ({ direction, id, itemId }) =>
                            itemId === row.id && id === col.id && direction === FilterFieldType.CELL,
                    );
                const cell = row.cells.find(({ id }: { id: number }) => id === col.id);
                newSet.id = cell.id;
                newSet.type = calcFilterType(cell, filterType);
                newSet.name = cell.str; // TODO check value for all types
                newSet.direction = FilterFieldType.CELL;
                newSet.itemId = row.id;
                newSet.answerGroupId = col.id;

                if (report.type === QuestionType.PAIR_COMPARISON) {
                    newSet.answerGroupId = cell.value2;
                }
            } else if (row) {
                existingSetIdx =
                    filter &&
                    filter.sets.findIndex(({ direction, id, itemId }) => {
                        if (withItems) {
                            return itemId === row.value1 + row.value2;
                        }
                        return id === row.id && direction === FilterFieldType.ROW;
                    });
                newSet.type = calcFilterType(row, filterType);
                newSet.id = row.id;
                // TODO better name mapping
                if (report.type === QuestionType.SMILE) {
                    const rowIdx = item.rows.findIndex(({ id }: { id: number }) => id === newSet.id);
                    newSet.name = row.value2 || rowIdx + 1;
                } else {
                    newSet.name = row.value1 || row.singleValue; // singleValue - Number
                }

                if (number) {
                    newSet.itemId = row.singleValue;
                }

                if (withItems) {
                    newSet.itemId = row.value1 + row.value2;
                }

                if (report.type === QuestionType.FIGMA_TEST) {
                    newSet.value = row.singleValue;
                }

                // value2 - residence answer_str
                newSet.direction = FilterFieldType.ROW;
                newSet.answerGroupId = semDiff ? undefined : row.groupId || row.value2;
            } else if (col) {
                existingSetIdx =
                    filter &&
                    filter.sets.findIndex(({ direction, id }) => id === col.id && direction === FilterFieldType.COL);
                newSet.type = calcFilterType(col, filterType);
                newSet.id = col.id;
                // TODO better name mapping
                newSet.name = col.npsType ? mapNpsLabel(col.npsType) : col.headValue;
                newSet.direction = FilterFieldType.COL;
                newSet.answerGroupId = col.id;
            } else {
                existingSetIdx =
                    filter &&
                    filter.sets.findIndex(({ direction, itemId }) => itemId === item.id && direction === null);
                newSet.id = report.id;
                newSet.name = report.name;
                newSet.type = calcFilterType(item, filterType);
                newSet.itemId = item.id;

                if (report.type === QuestionType.TEXT_PERCEPTION) {
                    existingSetIdx = filter && filter.sets.findIndex(({ value }) => value === item.value);
                    newSet.name = item.name;
                    newSet.value = item.value;

                    if (typeof existingSetIdx === 'number' && filter.sets[existingSetIdx]?.type === filterType) {
                        newSet.type = null;
                    }
                }
            }
    }

    if (!filter) {
        return [
            ...filters,
            {
                ...cloneDeep(initialBaseReport),
                id: report.id,
                name: report.name,
                sets: [newSet],
            },
        ];
    }

    return updateItemInArray(filters, report.id, (r: BaseReport) => {
        if (!withItems) {
            if (byCell) {
                // remove row\col filters then cell selected
                r.sets = r.sets.filter(({ direction }) => direction === FilterFieldType.CELL);
            } else if (filterFieldType !== FilterFieldType.CATEGORY) {
                // For CATEGORY filters (KANO), keep other CATEGORY filters
                r.sets = r.sets.filter(({ direction }) => direction !== FilterFieldType.CELL);
            }
        }

        if (existingSetIdx > -1) {
            if (!newSet.type) {
                r.sets.splice(existingSetIdx, 1);
            } else {
                r.sets.splice(existingSetIdx, 1, newSet);
            }
        } else {
            r.sets.push(newSet);
        }

        if (r.sets.length === 0) {
            return null;
        }

        return r;
    });
};

export const resetFilterType = <T>(
    items: Array<T & (Row | Col)>,
    item: Item,
    filter: BaseReport,
    set: FilterSet,
    dir: FilterFieldType,
): void => {
    const appliedSets = filter.sets.filter(({ direction, type }: FilterSet) => type !== null && direction === dir);

    items.forEach((i) => {
        if (i.id === set.id && item.id === set.itemId) {
            i.filterType = null;
        }
        // reset all row/col filters if one set left
        if (appliedSets.length === 1) {
            i.filterType = null;
        }
    });
};

export const resetAllFilters = (reports: Array<Report>): void =>
    reports.forEach((report) => {
        report = cloneDeep(report);
        report.comments.forEach((c) => {
            c.filterType = null;
        });
        report.items.forEach((item) => {
            item.filterType = null;
            item.rows.forEach((row) => {
                row.filterType = null;
                row.cells.forEach((c) => {
                    c.filterType = null;
                });
            });
            item.cols.forEach((col) => {
                col.filterType = null;
            });
            item.self.forEach((s) => {
                s.filterType = null;
            });
        });
    });

// TODO use immer
export const applyFiltersAndMarkSpoiled = (
    reports: Array<Report>,
    filters: Array<BaseReport>,
    questions: Array<Report>,
    upsertQuestions: boolean,
): Array<Report> => {
    filters = filters.map((f) => {
        const q = questions.find(({ id }) => id === f.id);
        const r = reports.find(({ id }) => id === f.id);

        if (upsertQuestions && q && !r) {
            // skip for not upsert
            return f;
        }

        if (!q) {
            f.sets.forEach((s) => {
                s.spoiled = true;

                return s;
            });

            return f;
        }

        f.sets.forEach((s) => {
            let match = null;
            const { cols, rows, type } = q.question;

            switch (s.direction) {
                case FilterFieldType.ROW:
                    match = rows.find(({ id }) => s.id === id);
                    break;
                case FilterFieldType.COL:
                    if (type === QuestionType.NPS) {
                        match = true;
                        break;
                    }
                    match = cols.find(({ id }) => s.id === id);
                    break;
                case FilterFieldType.CELL:
                    match = rows.find(({ id }) => s.itemId === id);
                    break;
                case null:
                    switch (s.filterFieldType) {
                        case FilterFieldType.SELF:
                            match = r && r.items.find((i) => i.self.find(({ id }) => id === s.id));
                            break;
                        case FilterFieldType.COMMENT:
                            match = r && r.comments.find(({ id }) => id === s.id);
                            break;
                    }
            }

            s.spoiled = !match;
        });

        return f;
    });

    reports = reports.filter((report) => !!report); // TODO wtf

    applyAllFilters(reports, filters);

    return reports;
};

export const applyAllFilters = (reports: Array<Report>, filters: Array<BaseReport>): void => {
    reports.forEach((report) => {
        const filter = filters.find((f) => f.id === report.id);
        const residence = report.type === QuestionType.RESIDENCE;
        const nested = report.type === QuestionType.NESTED_LIST;
        const withItems = residence || nested;
        const number = report.type === QuestionType.NUMBER;

        report.items.forEach((item) => {
            if (!filter) {
                return item;
            }

            const itemFilter = filter.sets.find(({ direction, itemId }) => direction === null && itemId === item.id);

            if (itemFilter) {
                switch (itemFilter.filterFieldType) {
                    case FilterFieldType.SELF:
                        item.self.forEach((s) => {
                            const matchForNumber = number && s.value1 === itemFilter.id; // TODO workaround, refactor
                            if (s.id !== itemFilter.id && !matchForNumber) {
                                return;
                            }
                            s.filterType = itemFilter.type;
                        });
                        break;
                    case FilterFieldType.COMMENT:
                        report.comments.forEach((c) => {
                            if (c.id !== itemFilter.itemId) {
                                return;
                            }
                            c.filterType = itemFilter.type;
                        });
                        break;
                    default:
                        item.filterType = itemFilter.type;
                }
            }

            // apply rows filters
            item.rows.forEach((row) => {
                const set = filter.sets.find(({ direction, id, itemId }) => {
                    if (withItems) {
                        return itemId === String(row.value1) + String(row.value2);
                    }
                    return direction === FilterFieldType.ROW && id === row.id && itemId === item.id;
                });

                row.filterType = set ? set.type : null;

                row.cells.forEach((c) => {
                    // For KANO category cells, find filter by category value
                    if (c.value?.category) {
                        const categoryFilter = filter.sets.find(
                            ({ filterFieldType, itemId, value }) =>
                                filterFieldType === FilterFieldType.CATEGORY &&
                                itemId === row.id &&
                                value?.category === c.value.category,
                        );
                        if (categoryFilter) {
                            c.filterType = categoryFilter.type;
                        }
                        return;
                    }

                    const cellFilter = filter.sets.find(
                        ({ direction, id, itemId }) =>
                            direction === FilterFieldType.CELL && id === c.id && itemId === row.id,
                    );

                    if (cellFilter) {
                        c.filterType = cellFilter.type;
                    }
                    if (cellFilter?.value) {
                        c.value = cellFilter.value;
                    }
                });
            });

            // apply cols filters
            item.cols.forEach((col) => {
                const set = filter.sets.find(
                    ({ direction, id, itemId }) =>
                        direction === FilterFieldType.COL && id === col.id && itemId === item.id,
                );

                col.filterType = set ? set.type : null;
            });
        });
    });
};
